import { useState, useEffect, useMemo, useCallback } from "react";
import TrendPeriodoChart from "../../../components/Graphs/TrendPeriodoChart/TrendPeriodoChart";
import AnimatedCounter from "../../../components/AnimatedCounter/AnimatedCounter";
import ProgressRing from "../../../components/ProgressRing/ProgressRing";
import axios from "axios";
import RotateDevice from "../../../components/RotateDevice/RotateDevice";

import "./TrendPeriodo.css";
import Lf23Loader from "../../../components/Lf23Loader";

/**
 * Componente TrendPeriodo - Gestisce la visualizzazione dei grafici e delle tabelle
 * per i dati delle operazioni HR con diverse viste (Mensile, Trimestrale, Annuale)
 *
 * Props:
 * - unfilteredHrData: array di dati grezzi dal backend
 * - loading: boolean per indicare lo stato di caricamento
 * - fromDate: string data inizio (formato yyyy-mm-dd) per calcoli della tabella
 * - toDate: string data fine (formato yyyy-mm-dd) per calcoli della tabella
 * - selectedTrimestri: array di trimestri selezionati per filtro frontend
 */
export const TrendPeriodo = ({
    unfilteredHrData = [],
    loading = false,
    fromDate,
    toDate,
    selectedTrimestri = [],
    selectedCategory = ""
}) => {
    // stati fullscreen
    const [isFullscreenChart, setIsFullscreenChart] = useState(false);
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenChart(false);
                setIsFullscreenTable(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreenChart, isFullscreenTable]);

    // Stati locali per la gestione della visualizzazione
    const [chartFilters, setChartFilters] = useState([]); // Array di filtri per multi-selezione
    const [tableViewType, setTableViewType] = useState('mese');
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const [legendSelection, setLegendSelection] = useState({
        'Ore lavorate': true,
        'Ore non lavorate': true,
        'Ore da contratto': true
    });

    /**
     * Callback per gestire il cambio di tipo vista del grafico (mese/trimestre/anno)
     * Reset del filtro quando cambia la vista del grafico
     * Nota: la vista della tabella è indipendente e gestita dai suoi bottoni M/T/A
     */
    const handleViewTypeChange = useCallback((viewType) => {
        setChartFilters([]);
    }, []);

    /**
     * Callback per gestire il filtro applicato dal grafico
     * filters può essere null (reset) o array di { type: 'mese'|'trimestre'|'anno', value: 'valore', anno?: number, key: string }
     */
    const handleChartFilterChange = useCallback((filters) => {
        setChartFilters(filters || []);
    }, []);

    /**
     * Rimuove un singolo filtro dalla selezione
     */
    const handleRemoveFilter = useCallback((filterKey) => {
        setChartFilters(prev => prev.filter(f => f.key !== filterKey));
    }, []);

    /**
     * Rimuove tutti i filtri
     */
    const handleClearAllFilters = useCallback(() => {
        setChartFilters([]);
    }, []);

    /**
     * Callback per gestire il cambio di selezione della legend del grafico
     * selected è un oggetto con lo stato di ogni serie: { "Ore lavorate": true, "Ore non lavorate": false, ... }
     */
    const handleLegendChange = useCallback((selected) => {
        setLegendSelection(selected);
    }, []);

    /* Funzione per effettuare il download del file  */
    const handleDownloadFile = async (data) => {
        try {
            const response = await axios.post(`${API_URL}/operation-HR/downloadExcel`,
                { tableName: selectedCategory, datiDaStampare: data },
                { responseType: "blob" }
            );

            if (response.data.type === "application/json") {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.error || "Errore durante il download");
            }

            // Estrai il nome del file dall'header Content-Disposition
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${selectedCategory}.xlsx`; // Default fallback

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1];
                }
            }

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Errore durante il download del file:", error);
            /* setMessage({
                type: "error",
                text: error.response?.data?.error || error.message || "Errore durante il download del file",
            }); */
        }
    };

    // Assicurati che unfilteredHrData sia sempre un array (può arrivare come oggetto da altra tab o null)
    const hrDataArray = useMemo(() => {
        return Array.isArray(unfilteredHrData) ? unfilteredHrData : [];
    }, [unfilteredHrData]);

    // Verifica se i dati sono nel formato corretto per questa tab (deve essere un array, non null/oggetto)
    // null = non ancora caricato, [] = caricato ma vuoto
    const hasValidDataFormat = unfilteredHrData !== null && Array.isArray(unfilteredHrData);

    /**
     * Memoizza i dati filtrati in base ai trimestri selezionati e ai filtri del grafico
     */
    const filteredData = useMemo(() => {
        let filtered = [...hrDataArray];

        // Filtra per trimestre (solo frontend, non gestito dal backend)
        if (selectedTrimestri.length > 0) {
            filtered = filtered.filter(item =>
                selectedTrimestri.some(t => t.value === item.trimestre)
            );
        }

        // Applica filtri dal grafico (se selezionati)
        if (chartFilters.length > 0) {
            filtered = filtered.filter(item => {
                // L'item deve corrispondere ad almeno uno dei filtri selezionati
                return chartFilters.some(filter => {
                    if (filter.type === 'mese') {
                        return item.mese === filter.value && item.anno === filter.anno;
                    } else if (filter.type === 'trimestre') {
                        return item.trimestre === filter.value;
                    } else if (filter.type === 'anno') {
                        return item.anno === filter.value;
                    }
                    return false;
                });
            });
        }

        return filtered;
    }, [hrDataArray, selectedTrimestri, chartFilters]);

    /**
     * Memoizza i dati preparati per la tabella riassuntiva
     * Gestisce tre diverse viste: mensile, trimestrale e annuale
     */
    const tableData = useMemo(() => {
        if (tableViewType === 'anno') {
            // Vista Annuale - mostra solo anni
            const anni = [...new Set(filteredData.map(d => d.anno))].sort();

            const oreLavorate = anni.map(anno => {
                const total = filteredData
                    .filter(d => d.anno === anno)
                    .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreNonLavorate = anni.map(anno => {
                const total = filteredData
                    .filter(d => d.anno === anno)
                    .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreDaContratto = anni.map(anno => {
                const total = filteredData
                    .filter(d => d.anno === anno)
                    .reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
                return Number(total.toFixed(1));
            });

            // Calcola percentuale ore non lavorate per ogni anno
            const percOreNonLavorate = anni.map((anno, idx) => {
                if (oreDaContratto[idx] === 0) return 0;
                return Number(((oreNonLavorate[idx] / oreDaContratto[idx]) * 100).toFixed(1));
            });

            // Calcola ore straordinarie per ogni anno: Ore_Lavorate + Ore_Non_Lavorate - Ore_Da_Contratto
            const oreStraordinarie = anni.map((anno, idx) => {
                const straord = oreLavorate[idx] + oreNonLavorate[idx] - oreDaContratto[idx];
                return Number(straord.toFixed(1));
            });

            // Calcola percentuale ore straordinarie per ogni anno
            const percOreStraordinarie = anni.map((anno, idx) => {
                if (oreDaContratto[idx] === 0) return 0;
                return Number(((oreStraordinarie[idx] / oreDaContratto[idx]) * 100).toFixed(1));
            });

            const totOreLavorate = oreLavorate.reduce((sum, val) => sum + val, 0);
            const totOreNonLavorate = oreNonLavorate.reduce((sum, val) => sum + val, 0);
            const totOreDaContratto = oreDaContratto.reduce((sum, val) => sum + val, 0);
            const totPercOreNonLavorate = totOreDaContratto === 0 ? 0 : Number(((totOreNonLavorate / totOreDaContratto) * 100).toFixed(1));
            const totOreStraordinarie = oreStraordinarie.reduce((sum, val) => sum + val, 0);
            const totPercOreStraordinarie = totOreDaContratto === 0 ? 0 : Number(((totOreStraordinarie / totOreDaContratto) * 100).toFixed(1));

            return {
                viewType: 'anno',
                periodi: anni,
                rows: [
                    {
                        categoria: 'Ore Lavorate',
                        valori: oreLavorate,
                        totale: Number(totOreLavorate.toFixed(1))
                    },
                    {
                        categoria: 'Ore Non Lavorate',
                        valori: oreNonLavorate,
                        totale: Number(totOreNonLavorate.toFixed(1))
                    },
                    {
                        categoria: '% Ore Non Lav',
                        valori: percOreNonLavorate,
                        totale: totPercOreNonLavorate
                    },
                    {
                        categoria: 'Ore da Contratto',
                        valori: oreDaContratto,
                        totale: Number(totOreDaContratto.toFixed(1))
                    },
                    {
                        categoria: 'Ore Straordinarie',
                        valori: oreStraordinarie,
                        totale: Number(totOreStraordinarie.toFixed(1))
                    },
                    {
                        categoria: '% Ore Straordinarie',
                        valori: percOreStraordinarie,
                        totale: Number(totPercOreStraordinarie.toFixed(1))
                    }
                ]
            };
        } else if (tableViewType === 'trimestre') {
            // Vista Trimestrale - mostra solo trimestri
            const trimestri = [...new Set(filteredData.map(d => d.trimestre))].sort((a, b) => {
                // a e b sono stringhe tipo "Trim 1 - 2025"
                // Divido la stringa per ottenere le parti
                const partiA = a.split(' '); // ["Trim", "1", "-", "2025"]
                const partiB = b.split(' ');

                const annoA = parseInt(partiA[3]); // 2025
                const annoB = parseInt(partiB[3]);
                const trimNumA = parseInt(partiA[1]); // 1
                const trimNumB = parseInt(partiB[1]);

                // Prima ordino per anno
                if (annoA !== annoB) {
                    return annoA - annoB;
                }
                // Se stesso anno, ordino per numero trimestre
                return trimNumA - trimNumB;
            });

            const oreLavorate = trimestri.map(trimestre => {
                const total = filteredData
                    .filter(d => d.trimestre === trimestre)
                    .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreNonLavorate = trimestri.map(trimestre => {
                const total = filteredData
                    .filter(d => d.trimestre === trimestre)
                    .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
                return Number(total.toFixed(1));
            });

            const oreDaContratto = trimestri.map(trimestre => {
                const total = filteredData
                    .filter(d => d.trimestre === trimestre)
                    .reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
                return Number(total.toFixed(1));
            });

            // Calcola percentuale ore non lavorate per ogni trimestre
            const percOreNonLavorate = trimestri.map((trimestre, idx) => {
                if (oreDaContratto[idx] === 0) return 0;
                return Number(((oreNonLavorate[idx] / oreDaContratto[idx]) * 100).toFixed(1));
            });

            // Calcola ore straordinarie per ogni trimestre
            const oreStraordinarie = trimestri.map((trimestre, idx) => {
                const straord = oreLavorate[idx] + oreNonLavorate[idx] - oreDaContratto[idx];
                return Number(straord.toFixed(1));
            });

            // Calcola percentuale ore straordinarie per ogni trimestre
            const percOreStraordinarie = trimestri.map((trimestre, idx) => {
                if (oreDaContratto[idx] === 0) return 0;
                return Number(((oreStraordinarie[idx] / oreDaContratto[idx]) * 100).toFixed(1));
            });

            const totOreLavorate = oreLavorate.reduce((sum, val) => sum + val, 0);
            const totOreNonLavorate = oreNonLavorate.reduce((sum, val) => sum + val, 0);
            const totOreDaContratto = oreDaContratto.reduce((sum, val) => sum + val, 0);
            const totPercOreNonLavorate = totOreDaContratto === 0 ? 0 : Number(((totOreNonLavorate / totOreDaContratto) * 100).toFixed(1));
            const totOreStraordinarie = oreStraordinarie.reduce((sum, val) => sum + val, 0);
            const totPercOreStraordinarie = totOreDaContratto === 0 ? 0 : Number(((totOreStraordinarie / totOreDaContratto) * 100).toFixed(1));

            return {
                viewType: 'trimestre',
                periodi: trimestri,
                rows: [
                    {
                        categoria: 'Ore Lavorate',
                        valori: oreLavorate,
                        totale: Number(totOreLavorate.toFixed(1))
                    },
                    {
                        categoria: 'Ore Non Lavorate',
                        valori: oreNonLavorate,
                        totale: Number(totOreNonLavorate.toFixed(1))
                    },
                    {
                        categoria: '% Ore Non Lav',
                        valori: percOreNonLavorate,
                        totale: totPercOreNonLavorate
                    },
                    {
                        categoria: 'Ore da Contratto',
                        valori: oreDaContratto,
                        totale: Number(totOreDaContratto.toFixed(1))
                    },
                    {
                        categoria: 'Ore Straordinarie',
                        valori: oreStraordinarie,
                        totale: Number(totOreStraordinarie.toFixed(1))
                    },
                    {
                        categoria: '% Ore Straordinarie',
                        valori: percOreStraordinarie,
                        totale: Number(totPercOreStraordinarie.toFixed(1))
                    }
                ]
            };
        }

        // Vista Mensile - gestisce correttamente più anni
        const mesiMap = {
            'Gennaio': 1, 'Febbraio': 2, 'Marzo': 3, 'Aprile': 4,
            'Maggio': 5, 'Giugno': 6, 'Luglio': 7, 'Agosto': 8,
            'Settembre': 9, 'Ottobre': 10, 'Novembre': 11, 'Dicembre': 12
        };

        const mesiAbbreviati = {
            'Gennaio': 'Gen', 'Febbraio': 'Feb', 'Marzo': 'Mar', 'Aprile': 'Apr',
            'Maggio': 'Mag', 'Giugno': 'Giu', 'Luglio': 'Lug', 'Agosto': 'Ago',
            'Settembre': 'Set', 'Ottobre': 'Ott', 'Novembre': 'Nov', 'Dicembre': 'Dic'
        };

        const meseToTrimestre = {
            'Gennaio': 'Trim 1', 'Febbraio': 'Trim 1', 'Marzo': 'Trim 1',
            'Aprile': 'Trim 2', 'Maggio': 'Trim 2', 'Giugno': 'Trim 2',
            'Luglio': 'Trim 3', 'Agosto': 'Trim 3', 'Settembre': 'Trim 3',
            'Ottobre': 'Trim 4', 'Novembre': 'Trim 4', 'Dicembre': 'Trim 4'
        };

        // Crea una lista di mesi unici con anno
        const mesiAnnoSet = new Set();
        filteredData.forEach(d => {
            mesiAnnoSet.add(`${d.mese}-${d.anno}`);
        });

        // Converti in array e ordina cronologicamente
        const mesiAnnoArray = Array.from(mesiAnnoSet).map(meseAnno => {
            const [mese, anno] = meseAnno.split('-');
            return {
                mese,
                anno: parseInt(anno),
                numeroMese: mesiMap[mese],
                label: `${mesiAbbreviati[mese]} ${anno}`,
                trimestre: meseToTrimestre[mese],
                annoTrimestre: `${anno}-${meseToTrimestre[mese]}`
            };
        }).sort((a, b) => {
            if (a.anno !== b.anno) return a.anno - b.anno;
            return a.numeroMese - b.numeroMese;
        });

        const mesiConInfo = mesiAnnoArray;
        const mesi = mesiAnnoArray.map(ma => ma.label);

        // Estrai anno da fromDate e toDate
        const annoInizio = new Date(fromDate).getFullYear();
        const annoFine = new Date(toDate).getFullYear();

        // Crea struttura di raggruppamento per anno e trimestre
        const anniGruppi = [];
        const trimGruppi = [];

        // Se gli anni di fromDate e toDate sono diversi, crea un unico gruppo con range
        if (annoInizio !== annoFine && mesiConInfo.length > 0) {
            anniGruppi.push({
                label: `${annoInizio} - ${annoFine}`,
                span: mesiConInfo.length + trimGruppi.length // Aggiungi spazio per colonne totali trim
            });
        } else {
            // Altrimenti usa la logica normale
            let currentAnno = null;
            let anniCount = 0;

            mesiConInfo.forEach(meseInfo => {
                if (currentAnno !== meseInfo.anno) {
                    if (anniCount > 0) {
                        anniGruppi.push({ label: currentAnno, span: anniCount });
                        anniCount = 0;
                    }
                    currentAnno = meseInfo.anno;
                }
                anniCount++;
            });

            // Aggiungi l'ultimo gruppo
            if (anniCount > 0) {
                anniGruppi.push({ label: currentAnno, span: anniCount });
            }
        }

        // Trimestri: logica normale
        let currentTrim = null;
        let trimCount = 0;

        mesiConInfo.forEach(meseInfo => {
            if (currentTrim !== meseInfo.annoTrimestre) {
                if (trimCount > 0) {
                    trimGruppi.push({ label: currentTrim.split('-')[1], span: trimCount });
                    trimCount = 0;
                }
                currentTrim = meseInfo.annoTrimestre;
            }
            trimCount++;
        });

        if (trimCount > 0) {
            trimGruppi.push({ label: currentTrim.split('-')[1], span: trimCount });
        }

        // Calcola ore per categoria, mese e anno
        const oreLavorate = mesiAnnoArray.map(ma => {
            const total = filteredData
                .filter(d => d.mese === ma.mese && d.anno === ma.anno)
                .reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
            return Number(total.toFixed(1));
        });

        const oreNonLavorate = mesiAnnoArray.map(ma => {
            const total = filteredData
                .filter(d => d.mese === ma.mese && d.anno === ma.anno)
                .reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
            return Number(total.toFixed(1));
        });

        const oreDaContratto = mesiAnnoArray.map(ma => {
            const total = filteredData
                .filter(d => d.mese === ma.mese && d.anno === ma.anno)
                .reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
            return Number(total.toFixed(1));
        });

        // Calcola percentuale ore non lavorate per ogni mese
        const percOreNonLavorate = mesiAnnoArray.map((ma, idx) => {
            if (oreDaContratto[idx] === 0) return 0;
            return Number(((oreNonLavorate[idx] / oreDaContratto[idx]) * 100).toFixed(1));
        });

        // Calcola ore straordinarie per ogni mese
        const oreStraordinarie = mesiAnnoArray.map((ma, idx) => {
            const straord = oreLavorate[idx] + oreNonLavorate[idx] - oreDaContratto[idx];
            return Number(straord.toFixed(1));
        });

        // Calcola percentuale ore straordinarie per ogni mese
        const percOreStraordinarie = mesiAnnoArray.map((ma, idx) => {
            if (oreDaContratto[idx] === 0) return 0;
            return Number(((oreStraordinarie[idx] / oreDaContratto[idx]) * 100).toFixed(1));
        });

        // Calcola totali per trimestre
        const calcTotaliTrimestre = (valori) => {
            const totali = [];
            let startIdx = 0;

            trimGruppi.forEach(gruppo => {
                const endIdx = startIdx + gruppo.span;
                const sommaTrim = valori.slice(startIdx, endIdx).reduce((sum, val) => sum + val, 0);
                totali.push(Number(sommaTrim.toFixed(1)));
                startIdx = endIdx;
            });

            return totali;
        };

        const totaliTrimOreLavorate = calcTotaliTrimestre(oreLavorate);
        const totaliTrimOreNonLavorate = calcTotaliTrimestre(oreNonLavorate);
        const totaliTrimOreDaContratto = calcTotaliTrimestre(oreDaContratto);
        const totaliTrimOreStraordinarie = calcTotaliTrimestre(oreStraordinarie);

        // Calcola percentuale ore non lavorate per ogni trimestre
        const totaliTrimPercOreNonLavorate = totaliTrimOreNonLavorate.map((oreNonLav, idx) => {
            if (totaliTrimOreDaContratto[idx] === 0) return 0;
            return Number(((oreNonLav / totaliTrimOreDaContratto[idx]) * 100).toFixed(1));
        });

        // Calcola percentuale ore straordinarie per ogni trimestre
        const totaliTrimPercOreStraordinarie = totaliTrimOreStraordinarie.map((oreStraord, idx) => {
            if (totaliTrimOreDaContratto[idx] === 0) return 0;
            return Number(((oreStraord / totaliTrimOreDaContratto[idx]) * 100).toFixed(1));
        });

        // Calcola totali periodo
        const totOreLavorate = oreLavorate.reduce((sum, val) => sum + val, 0);
        const totOreNonLavorate = oreNonLavorate.reduce((sum, val) => sum + val, 0);
        const totOreDaContratto = oreDaContratto.reduce((sum, val) => sum + val, 0);
        const totPercOreNonLavorate = totOreDaContratto === 0 ? 0 : Number(((totOreNonLavorate / totOreDaContratto) * 100).toFixed(1));
        const totOreStraordinarie = oreStraordinarie.reduce((sum, val) => sum + val, 0);
        const totPercOreStraordinarie = totOreDaContratto === 0 ? 0 : Number(((totOreStraordinarie / totOreDaContratto) * 100).toFixed(1));

        // Aggiorna span degli anniGruppi per includere le colonne dei totali trimestrali
        const numTrimestri = trimGruppi.length;
        anniGruppi.forEach(gruppo => {
            gruppo.span = gruppo.span + numTrimestri;
        });

        return {
            viewType: 'mese',
            mesi,
            anniGruppi,
            trimGruppi,
            rows: [
                {
                    categoria: 'Ore Lavorate',
                    valori: oreLavorate,
                    totaliTrimestre: totaliTrimOreLavorate,
                    totale: Number(totOreLavorate.toFixed(1))
                },
                {
                    categoria: 'Ore Non Lavorate',
                    valori: oreNonLavorate,
                    totaliTrimestre: totaliTrimOreNonLavorate,
                    totale: Number(totOreNonLavorate.toFixed(1))
                },
                {
                    categoria: '% Ore Non Lav',
                    valori: percOreNonLavorate,
                    totaliTrimestre: totaliTrimPercOreNonLavorate,
                    totale: totPercOreNonLavorate
                },
                {
                    categoria: 'Ore da Contratto',
                    valori: oreDaContratto,
                    totaliTrimestre: totaliTrimOreDaContratto,
                    totale: Number(totOreDaContratto.toFixed(1))
                },
                {
                    categoria: 'Ore Straordinarie',
                    valori: oreStraordinarie,
                    totaliTrimestre: totaliTrimOreStraordinarie,
                    totale: Number(totOreStraordinarie.toFixed(1))
                },
                {
                    categoria: '% Ore Straordinarie',
                    valori: percOreStraordinarie,
                    totaliTrimestre: totaliTrimPercOreStraordinarie,
                    totale: totPercOreStraordinarie
                }
            ]
        };
    }, [filteredData, fromDate, toDate, tableViewType]);

    /**
     * Filtra le righe della tabella in base alla selezione della legend del grafico
     * Nasconde le righe delle serie non selezionate e le loro percentuali correlate
     */
    const filteredTableData = useMemo(() => {
        if (!tableData) return tableData;

        const filteredRows = tableData.rows.filter(row => {
            const cat = row.categoria;

            // Mappa le categorie della tabella alle voci della legend
            if (cat === 'Ore Lavorate') return legendSelection['Ore lavorate'];
            if (cat === 'Ore Non Lavorate') return legendSelection['Ore non lavorate'];
            if (cat === '% Ore Non Lav') return legendSelection['Ore non lavorate'];
            if (cat === 'Ore da Contratto') return legendSelection['Ore da contratto'];
            if (cat === 'Ore Straordinarie') return legendSelection['Ore lavorate'] || legendSelection['Ore non lavorate'];
            if (cat === '% Ore Straordinarie') return legendSelection['Ore lavorate'] || legendSelection['Ore non lavorate'];

            return true;
        });

        return {
            ...tableData,
            rows: filteredRows
        };
    }, [tableData, legendSelection]);

    /**
     * Calcola i KPI riassuntivi per le summary cards
     */
    const kpiSummary = useMemo(() => {
        if (filteredData.length === 0) {
            return {
                oreTotali: 0,
                oreLavorate: 0,
                oreNonLavorate: 0,
                oreDaContratto: 0,
                oreStraordinarie: 0,
                percNonLavorate: 0,
                percStraordinarie: 0
            };
        }

        const oreLavorate = filteredData.reduce((sum, d) => sum + (d.oreLavorate || 0), 0);
        const oreNonLavorate = filteredData.reduce((sum, d) => sum + (d.oreNonLavorate || 0), 0);
        const oreDaContratto = filteredData.reduce((sum, d) => sum + (d.oreDaContratto || 0), 0);
        const oreTotali = oreLavorate + oreNonLavorate;
        const oreStraordinarie = oreTotali - oreDaContratto;
        const percNonLavorate = oreDaContratto > 0 ? (oreNonLavorate / oreDaContratto) * 100 : 0;
        const percStraordinarie = oreDaContratto > 0 ? (oreStraordinarie / oreDaContratto) * 100 : 0;

        return {
            oreTotali: Number(oreTotali.toFixed(1)),
            oreLavorate: Number(oreLavorate.toFixed(1)),
            oreNonLavorate: Number(oreNonLavorate.toFixed(1)),
            oreDaContratto: Number(oreDaContratto.toFixed(1)),
            oreStraordinarie: Number(oreStraordinarie.toFixed(1)),
            percNonLavorate: Number(percNonLavorate.toFixed(1)),
            percStraordinarie: Number(percStraordinarie.toFixed(1))
        };
    }, [filteredData]);

    /**
     * Formatta i numeri con separatore migliaia (.) e decimali (,)
     * Massimo 2 cifre decimali con arrotondamento
     */
    const formatNumber = (num) => {
        return new Intl.NumberFormat('it-IT', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            useGrouping: true
        }).format(num);
    };

    /**
     * Ritorna la classe CSS per la riga in base alla categoria
     */
    const getRowClass = (categoria) => {
        switch (categoria) {
            case 'Ore Lavorate':
                return 'row-ore-lavorate';
            case 'Ore Non Lavorate':
                return 'row-ore-non-lavorate';
            case '% Ore Non Lav':
                return 'row-perc-non-lav';
            case 'Ore da Contratto':
                return 'row-ore-contratto';
            case 'Ore Straordinarie':
                return 'row-ore-straordinarie';
            case '% Ore Straordinarie':
                return 'row-perc-straordinarie';
            default:
                return '';
        }
    };

    /**
     * Ritorna la classe CSS per la cella in base al valore e alla categoria
     * Evidenzia valori anomali (es. % ore non lavorate > 10% o > 15%)
     */
    const getCellClass = (categoria, valore) => {
        // Per % Ore Non Lavorate: warning se > 10%, danger se > 15%
        if (categoria === '% Ore Non Lav') {
            if (valore > 15) return 'value-danger';
            if (valore > 10) return 'value-high';
        }

        // Per Ore Straordinarie: sempre rosso soft
        if (categoria === 'Ore Straordinarie') {
            return 'value-straordinari';
        }

        // Per % Ore Straordinarie: sempre rosso soft
        if (categoria === '% Ore Straordinarie') {
            return 'value-straordinari';
        }

        return '';
    };

    // Stato di caricamento e dati
    // Mostra loader se: sta caricando OPPURE i dati non sono nel formato corretto (es. oggetto da altra tab)
    const isLoading = loading || !hasValidDataFormat;
    const isDataReady = !loading && hasValidDataFormat && hrDataArray.length > 0;
    const isEmpty = !loading && hasValidDataFormat && hrDataArray.length === 0;

    return (
        <div className="chart-table-container">
            {isDataReady ? (
                <>
                    {/* KPI Summary Cards */}
                    <div className="oper-hr-kpi-cards">
                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon contratto">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                    <polyline points="10 9 9 9 8 9"></polyline>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore da Contratto</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.oreDaContratto} decimals={1} suffix=" h" />
                                </span>
                                <span className="oper-hr-kpi-sublabel">Ore pianificate</span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon lavorate">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Lavorate</span>
                                <span className="oper-hr-kpi-value">
                                    <AnimatedCounter value={kpiSummary.oreLavorate} decimals={1} suffix=" h" />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    {kpiSummary.oreDaContratto > 0
                                        ? <><AnimatedCounter value={(kpiSummary.oreLavorate / kpiSummary.oreDaContratto) * 100} decimals={1} suffix="%" /> del contratto</>
                                        : 'N/D'
                                    }
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card oper-hr-kpi-card-with-ring">
                            <ProgressRing
                                value={kpiSummary.percNonLavorate}
                                size={56}
                                strokeWidth={5}
                                thresholds={{ warning: 5, danger: 15 }}
                                inverse={true}
                                label="Assenteismo"
                            />
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Non Lavorate</span>
                                <span className="oper-hr-kpi-sublabel">
                                    <AnimatedCounter value={kpiSummary.oreNonLavorate} decimals={1} suffix=" h" />
                                </span>
                            </div>
                        </div>

                        <div className="oper-hr-kpi-card">
                            <div className="oper-hr-kpi-icon straordinarie-pos">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                                </svg>
                            </div>
                            <div className="oper-hr-kpi-content">
                                <span className="oper-hr-kpi-label">Ore Straordinarie</span>
                                <span className="oper-hr-kpi-value straordinari">
                                    <AnimatedCounter
                                        value={kpiSummary.oreStraordinarie}
                                        decimals={1}
                                        prefix={kpiSummary.oreStraordinarie > 0 ? '+' : ''}
                                        suffix=" h"
                                    />
                                </span>
                                <span className="oper-hr-kpi-sublabel">
                                    <AnimatedCounter
                                        value={kpiSummary.percStraordinarie}
                                        decimals={1}
                                        prefix={kpiSummary.percStraordinarie > 0 ? '+' : ''}
                                        suffix="% vs contratto"
                                    />
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Badge filtri attivi */}
                    {chartFilters.length > 0 && (
                        <div className="oper-hr-active-filter">
                            <span className="oper-hr-active-filter-label">Filtrando per:</span>
                            <div className="oper-hr-active-filter-tags">
                                {chartFilters.map(filter => {
                                    const label = filter.type === 'mese'
                                        ? `${filter.value} ${filter.anno}`
                                        : filter.type === 'trimestre'
                                            ? filter.value
                                            : `Anno ${filter.value}`;
                                    return (
                                        <span key={filter.key} className="oper-hr-active-filter-badge" title={label}>
                                            <span className="oper-hr-active-filter-text">{label}</span>
                                            <button
                                                className="oper-hr-active-filter-remove"
                                                onClick={() => handleRemoveFilter(filter.key)}
                                                title="Rimuovi filtro"
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                                </svg>
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                            {chartFilters.length > 1 && (
                                <button
                                    className="oper-hr-clear-all-filters"
                                    onClick={handleClearAllFilters}
                                    title="Rimuovi tutti i filtri"
                                >
                                    Rimuovi tutti
                                </button>
                            )}
                        </div>
                    )}

                    {/* Grafico a barre con dati non filtrati */}
                    <div className={`trend-periodo-chart-wrapper${isFullscreenChart ? ' fullscreen-trend-periodo' : ''}`}>
                        <RotateDevice active={isFullscreenChart} />
                        <TrendPeriodoChart
                            hrData={hrDataArray}
                            onViewTypeChange={handleViewTypeChange}
                            onChartFilterChange={handleChartFilterChange}
                            onLegendChange={handleLegendChange}
                            onDownload={handleDownloadFile}
                            extraButton={
                                <button
                                    className="trend-periodo-fullscreen-btn"
                                    onClick={() => setIsFullscreenChart(!isFullscreenChart)}
                                    title={isFullscreenChart ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreenChart ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            }
                        />
                    </div>

                    {/* Titolo e bottoni per cambio vista tabella */}
                    <div className={`trend-periodo-table-section${isFullscreenTable ? ' fullscreen-trend-periodo' : ''}`}>
                        <RotateDevice active={isFullscreenTable} />
                    <div className="oper-hr-table-title-container">
                        <span className="oper-hr-table-title">Trend Ore e KPI</span>
                        <div className="oper-hr-view-type-excel">
                            <div className="oper-hr-table-view-type-container">
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${tableViewType === 'mese' ? 'active' : ''}`}
                                    onClick={() => setTableViewType('mese')}
                                    title="Vista Mensile"
                                >
                                    <span>M</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${tableViewType === 'trimestre' ? 'active' : ''}`}
                                    onClick={() => setTableViewType('trimestre')}
                                    title="Vista Trimestrale"
                                >
                                    <span>T</span>
                                </button>
                                <button
                                    type="button"
                                    className={`oper-hr-table-view-btn ${tableViewType === 'anno' ? 'active' : ''}`}
                                    onClick={() => setTableViewType('anno')}
                                    title="Vista Annuale"
                                >
                                    <span>A</span>
                                </button>
                            </div>
                            <div className="esporta-file-excel">
                                <button className="hera-download-btn" onClick={() => handleDownloadFile(filteredTableData)} disabled={filteredData.length === 0}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="7 10 12 15 17 10"></polyline>
                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                    Esporta
                                </button>
                            </div>
                            <button
                                className="trend-periodo-fullscreen-btn"
                                onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenTable ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Tabella riassuntiva con dati filtrati */}
                    {filteredData.length === 0 ? (
                        <div className="oper-hr-empty-state">
                            <div className="oper-hr-empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="9" y1="9" x2="15" y2="15"></line>
                                    <line x1="15" y1="9" x2="9" y2="15"></line>
                                </svg>
                            </div>
                            <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                            <p className="oper-hr-empty-text">
                                {chartFilters.length > 0
                                    ? `Non ci sono dati per i periodi selezionati. Prova a rimuovere i filtri o selezionare altri periodi.`
                                    : `Seleziona un intervallo di date e applica i filtri per visualizzare i dati.`
                                }
                            </p>
                            {chartFilters.length > 0 && (
                                <button
                                    className="oper-hr-empty-btn"
                                    onClick={handleClearAllFilters}
                                >
                                    Rimuovi filtri
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="oper-hr-table-wrapper">
                            <table className="oper-hr-table">
                                <thead>
                                    {tableViewType === 'mese' ? (
                                        <>
                                            {/* Vista Mensile - Riga Anno */}
                                            <tr>
                                                <th>Anno</th>
                                                {tableData.anniGruppi.map((gruppo, index) => (
                                                    <th key={index} colSpan={gruppo.span}>{gruppo.label}</th>
                                                ))}
                                                <th rowSpan="3">Totale</th>
                                            </tr>
                                            {/* Riga Trimestre */}
                                            <tr>
                                                <th>Trimestre</th>
                                                {tableData.trimGruppi.map((gruppo, index) => (
                                                    <th key={index} colSpan={gruppo.span + 1}>{gruppo.label}</th>
                                                ))}
                                            </tr>
                                            {/* Riga Mesi */}
                                            <tr>
                                                <th></th>
                                                {(() => {
                                                    const headers = [];
                                                    let meseIdx = 0;

                                                    tableData.trimGruppi.forEach((gruppo, trimIdx) => {
                                                        // Mesi del trimestre
                                                        for (let i = 0; i < gruppo.span; i++) {
                                                            headers.push(
                                                                <th key={`mese-${meseIdx}`}>{tableData.mesi[meseIdx]}</th>
                                                            );
                                                            meseIdx++;
                                                        }
                                                        // Colonna totale trimestre
                                                        headers.push(
                                                            <th key={`tot-trim-${trimIdx}`} className="totale-trimestre">Tot. Trim</th>
                                                        );
                                                    });

                                                    return headers;
                                                })()}
                                            </tr>
                                        </>
                                    ) : (
                                        <>
                                            {/* Vista Trimestre o Anno - Header semplificato */}
                                            <tr>
                                                <th>{tableViewType === 'trimestre' ? 'Trimestre' : 'Anno'}</th>
                                                {tableData.periodi.map((periodo, index) => (
                                                    <th key={index}>{periodo}</th>
                                                ))}
                                                <th>Totale</th>
                                            </tr>
                                        </>
                                    )}
                                </thead>
                                <tbody>
                                    {tableViewType === 'mese' ? (
                                        // Vista Mensile - rendering complesso
                                        filteredTableData.rows.map((row, rowIndex) => {
                                            const isPercentuale = row.categoria === '% Ore Non Lav' || row.categoria === '% Ore Straordinarie';
                                            const rowClass = getRowClass(row.categoria);

                                            return (
                                                <tr key={rowIndex} className={rowClass}>
                                                    <td>{row.categoria}</td>
                                                    {(() => {
                                                        const cells = [];
                                                        let valoreIdx = 0;

                                                        tableData.trimGruppi.forEach((gruppo, trimIdx) => {
                                                            // Valori dei mesi del trimestre
                                                            for (let i = 0; i < gruppo.span; i++) {
                                                                const valore = row.valori[valoreIdx];
                                                                const cellClass = getCellClass(row.categoria, valore);
                                                                cells.push(
                                                                    <td key={`val-${valoreIdx}`} className={cellClass}>
                                                                        {formatNumber(valore)}{isPercentuale ? '%' : ''}
                                                                    </td>
                                                                );
                                                                valoreIdx++;
                                                            }
                                                            // Totale del trimestre
                                                            const trimCellClass = getCellClass(row.categoria, row.totaliTrimestre[trimIdx]);
                                                            cells.push(
                                                                <td key={`tot-trim-${trimIdx}`} className={`totale-trimestre ${trimCellClass}`}>
                                                                    <strong>{formatNumber(row.totaliTrimestre[trimIdx])}{isPercentuale ? '%' : ''}</strong>
                                                                </td>
                                                            );
                                                        });

                                                        return cells;
                                                    })()}
                                                    <td className={getCellClass(row.categoria, row.totale)}>
                                                        <strong>{formatNumber(row.totale)}{isPercentuale ? '%' : ''}</strong>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        // Vista Trimestre o Anno - rendering semplice
                                        filteredTableData.rows.map((row, rowIndex) => {
                                            const isPercentuale = row.categoria === '% Ore Non Lav' || row.categoria === '% Ore Straordinarie';
                                            const rowClass = getRowClass(row.categoria);

                                            return (
                                                <tr key={rowIndex} className={rowClass}>
                                                    <td>{row.categoria}</td>
                                                    {row.valori.map((valore, idx) => {
                                                        const cellClass = getCellClass(row.categoria, valore);
                                                        return (
                                                            <td key={idx} className={cellClass}>
                                                                {formatNumber(valore)}{isPercentuale ? '%' : ''}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className={getCellClass(row.categoria, row.totale)}>
                                                        <strong>{formatNumber(row.totale)}{isPercentuale ? '%' : ''}</strong>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    </div>
                </>
            ) : isLoading ? (
                <Lf23Loader />
            ) : isEmpty ? (
                <div className="oper-hr-empty-state">
                    <div className="oper-hr-empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="9" y1="9" x2="15" y2="15"></line>
                            <line x1="15" y1="9" x2="9" y2="15"></line>
                        </svg>
                    </div>
                    <h4 className="oper-hr-empty-title">Nessun dato disponibile</h4>
                    <p className="oper-hr-empty-text">
                        Non ci sono dati per i filtri selezionati. Prova a modificare i filtri.
                    </p>
                </div>
            ) : (
                <Lf23Loader />
            )}
        </div>
    );
};

export default TrendPeriodo;